import dayjs from 'dayjs';

export interface UserAuthStatus {
    noAuth: boolean;
    orgPass: boolean;
    personPass: boolean;
    personPending: boolean;
    personRejected: boolean;
}

export interface UserAuthDisplayInfo {
    identityCode: string;
    identityName: string;
    name: string;
    hospital: string;
    office: string;
    job: string;
    authTime: string;
    identityPath: string;
}

/** 判断用户认证状态（对齐 selection-web） */
export const getUserAuthStatus = (user: any): UserAuthStatus => {
    const baseInfo = user?.baseInfo;
    if (!baseInfo) {
        return { noAuth: true, orgPass: false, personPass: false, personPending: false, personRejected: false };
    }

    const noAuth = !baseInfo.memAuthStatus || (baseInfo.memType === 1 && baseInfo.memIsyijiehui === 2);
    const personPass = baseInfo.memType === 1 && baseInfo.memIsyijiehui === 1;
    const orgPass = baseInfo.memType === 2 && baseInfo.memAuthStatus === 2;
    const personPending =
        (baseInfo.memType === 1 && baseInfo.memIsyijiehui === 3) || (baseInfo.memType === 2 && baseInfo.memAuthStatus === 1);
    const personRejected =
        (baseInfo.memType === 1 && baseInfo.memIsyijiehui === 4) || (baseInfo.memType === 2 && baseInfo.memAuthStatus === 3);

    return { noAuth, orgPass, personPass, personPending, personRejected };
};

/**
 * 检查认证审核时间是否超过 1 年且未确认。
 * 调用方须先确认 personPass，未通过认证的用户不应进入本判断。
 */
export const isAuthExpired = (user: any): boolean => {
    const authTime = user?.baseInfo?.memExamineTime;
    if (!authTime) {
        return false;
    }
    const authMoment = dayjs(authTime);
    if (!authMoment.isValid()) {
        return false;
    }
    const diffYears = dayjs().diff(authMoment, 'year', true);
    return diffYears >= 1 && !user?.baseInfo?.memAuthEdit;
};

/** 是否需要弹出认证信息确认（仅个人认证已通过且超期） */
export const shouldShowAuthInfoConfirm = (user: any): boolean => {
    const { personPass } = getUserAuthStatus(user);
    return personPass && isAuthExpired(user);
};

const formatAuthTime = (time?: string) => {
    if (!time) {
        return '';
    }
    const m = dayjs(time);
    if (!m.isValid()) {
        return '';
    }
    return m.format('YYYY年M月D日 HH:mm');
};

/** 将认证身份字典 options 转为 code -> name 映射 */
export const buildIdentityNameMap = (options?: Array<{ code?: string; name?: string }>): Record<string, string> => {
    const map: Record<string, string> = {};
    if (!Array.isArray(options)) {
        return map;
    }
    options.forEach(opt => {
        if (opt?.code && opt?.name) {
            map[opt.code] = opt.name;
        }
    });
    return map;
};

/** 组装认证信息回显字段 */
export const getUserAuthDisplayInfo = (user: any, identityNameMap?: Record<string, string>): UserAuthDisplayInfo | null => {
    if (!user?.baseInfo) {
        return null;
    }
    const baseInfo = user.baseInfo;
    const qitaMap = baseInfo.qitaMap || {};
    const identityCode = qitaMap.memIdentity || '';
    const identityName =
        identityNameMap?.[identityCode] || (identityCode === 'A01' ? '医疗机构从业者' : identityCode);

    return {
        identityCode,
        identityName,
        name: baseInfo.memNickname || '',
        hospital: qitaMap.memUnit || '',
        office: qitaMap.memOffice || '',
        job: qitaMap.memPost || '',
        authTime: formatAuthTime(baseInfo.memExamineTime),
        identityPath: user.authInfo?.memIdentityPath || '',
    };
};

/**
 * 拼接认证资料 / OSS 图片完整 URL。
 * - 以 http 开头：视为完整 URL，原样返回
 * - 否则：`{ossPrefix}/{相对路径}`（不额外加前导 /）
 */
export const resolveOssUrl = (imagePath: string, ossUrlPrefix: string): string => {
    if (!imagePath) {
        return '';
    }
    const path = imagePath.trim();
    if (!path) {
        return '';
    }
    if (path.startsWith('http')) {
        return path;
    }
    const prefix = (ossUrlPrefix || '').replace(/\/+$/, '');
    const relativePath = path.replace(/^\/+/, '');
    if (!prefix) {
        return relativePath;
    }
    return `${prefix}/${relativePath}`;
};

/** @deprecated 使用 resolveOssUrl */
export const resolveAuthIdentityUrl = resolveOssUrl;
